import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, History, Plus, Settings2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import {
  createVerifier,
  fetchVerifiers,
  saveVerifierSettings,
  VERIF_STR as S,
  type VerifierRow,
} from './verifApi'

/**
 * A9.09 + A9.10 — Uchinchi tomon verifikatorlar + verifikator sozlamalari
 * (ikonka, kompaniya). Ro'yxat (A9.09) va har birining sozlamasi (A9.10) bir
 * ekranда. Backend `/admin/verifiers*` greenfield → fetch fail-soft (bo'sh),
 * saqlash no-op (endpoint chiqqach avtomatik ishlaydi).
 */
export function ThirdPartyVerifiers() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [edit, setEdit] = useState<VerifierRow | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const mayManage = can(PERMISSIONS.USERS_BAN)

  const columns = useMemo<AdminColumn<VerifierRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id', width: 70 },
      {
        key: 'company',
        header: S.company,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <span className="flex items-center gap-2">
            {r.icon_url ? (
              <img src={r.icon_url} alt="" className="h-5 w-5 rounded object-cover" />
            ) : (
              <span className="flex h-5 w-5 items-center justify-center rounded bg-surface-3">
                <Building2 size={12} className="text-tertiary" />
              </span>
            )}
            <span className="text-primary">{r.company}</span>
          </span>
        ),
      },
      { key: 'domain', header: S.domain, type: 'text', width: 200, render: (r) => r.domain || '—' },
      { key: 'granted_count', header: S.grantedCount, type: 'number', width: 130 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'active', label: S.active, status: 'active' },
          { value: 'suspended', label: S.suspended, status: 'suspended' },
        ],
      },
      { key: 'created_at', header: S.created2, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<VerifierRow>[]>(
    () => [
      {
        id: 'settings',
        label: S.settings,
        icon: <Settings2 size={14} />,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (r) => setEdit(r),
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchVerifiers, [])

  return (
    <>
      <PageHeader
        title={S.verifiers}
        screenId="A9.09"
        description={S.verifiersNote}
        actions={
          <div className="flex items-center gap-1.5">
            {mayManage && (
              <button
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
              >
                <Plus size={13} />
                {S.addVerifier}
              </button>
            )}
            <Link
              to="/verification/verifiers/audit"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <History size={13} />
              {S.audit}
            </Link>
          </div>
        }
      />
      <DataTable<VerifierRow>
        screenId="A9.09"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Building2 size={28} strokeWidth={1.5} /> }}
      />

      {edit && <SettingsDialog row={edit} onClose={() => setEdit(null)} toast={toast} />}
      {addOpen && <SettingsDialog row={null} onClose={() => setAddOpen(false)} toast={toast} />}
    </>
  )
}

/** A9.10 — sozlamalar (ikonka, kompaniya, domen). `row=null` → yangi qo'shish. */
function SettingsDialog({
  row,
  onClose,
  toast,
}: {
  row: VerifierRow | null
  onClose: () => void
  toast: ReturnType<typeof useToast>['toast']
}) {
  const [company, setCompany] = useState(row?.company ?? '')
  const [iconUrl, setIconUrl] = useState(row?.icon_url ?? '')
  const [domain, setDomain] = useState(row?.domain ?? '')
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const submit = async () => {
    if (!company.trim()) return
    setBusy(true)
    const patch = { company: company.trim(), icon_url: iconUrl.trim(), domain: domain.trim() }
    const r = row ? await saveVerifierSettings(row.id, patch) : await createVerifier(patch)
    setBusy(false)
    if (r.ok) {
      toast(S.saved, { tone: 'success' })
      onClose()
    } else {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={row ? S.settingsTitle : S.addVerifier}
      icon={<Settings2 size={18} className="text-accent" />}
      initialFocusRef={inputRef}
      footer={
        <>
          <Button onClick={onClose}>{S.back}</Button>
          <Button variant="primary" disabled={busy || !company.trim()} onClick={submit}>
            {S.save}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs text-tertiary">{S.company}</span>
          <input
            ref={inputRef}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-tertiary">{S.iconUrl}</span>
          <input
            value={iconUrl}
            onChange={(e) => setIconUrl(e.target.value)}
            placeholder="https://…/icon.png"
            className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-tertiary">{S.domain}</span>
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
        </label>
        {iconUrl.trim() && (
          <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-1 p-2">
            <span className="text-xs text-tertiary">{S.icon}:</span>
            <img src={iconUrl} alt="" className="h-6 w-6 rounded object-cover" />
          </div>
        )}
      </div>
    </Dialog>
  )
}
